import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { classAPI } from '../services/api';

const ClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('card'); // 'card' 또는 'list'
  
  const [formData, setFormData] = useState({
    name: '',
    duration: 60,
    fee: 0,
    color: '#90caf9',
    memo: '',
  });

  // 수업 목록 조회
  const fetchClasses = async () => {
    try {
      setLoading(true);
      console.log('수업 목록 조회 시작...'); // 디버깅용 로그
      const response = await classAPI.getClasses();
      console.log('수업 목록 응답:', response.data); // 디버깅용 로그
      setClasses(response.data.classes);
    } catch (error) {
      console.error('수업 목록 조회 실패:', error);
      console.error('오류 상세:', error.response?.data); // 더 자세한 오류 정보
      showSnackbar(`수업 목록 조회에 실패했습니다: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      duration: 60,
      fee: 0,
      color: '#90caf9',
      memo: '',
    });
    setEditingClass(null);
  };

  // 다이얼로그 열기
  const handleOpenDialog = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setFormData({
        name: cls.name,
        duration: cls.duration,
        fee: cls.fee,
        color: cls.color || '#90caf9',
        memo: cls.memo || '',
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  // 폼 데이터 변경
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 수업 저장
  const handleSave = async () => {
    try {
      // 필수 필드 검증
      if (!formData.name || !formData.name.trim()) {
        showSnackbar('수업명을 입력해주세요.', 'error');
        return;
      }
      
      if (!formData.duration || formData.duration <= 0) {
        showSnackbar('수업 시간을 올바르게 입력해주세요.', 'error');
        return;
      }
      
      if (!formData.fee || formData.fee < 0) {
        showSnackbar('수강료를 올바르게 입력해주세요.', 'error');
        return;
      }
      
      console.log('저장할 데이터:', formData); // 디버깅용 로그 추가
      
      if (editingClass) {
        await classAPI.updateClass(editingClass.id, formData);
        showSnackbar('수업 정보가 수정되었습니다.', 'success');
      } else {
        const response = await classAPI.createClass(formData);
        console.log('API 응답:', response); // 디버깅용 로그 추가
        showSnackbar('수업이 등록되었습니다.', 'success');
      }
      
      fetchClasses();
      handleCloseDialog();
    } catch (error) {
      console.error('수업 저장 실패:', error);
      console.error('오류 상세:', error.response?.data); // 더 자세한 오류 정보
      showSnackbar(`수업 저장에 실패했습니다: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  // 수업 삭제
  const handleDelete = async (classId) => {
    if (window.confirm('정말로 이 수업을 삭제하시겠습니까?')) {
      try {
        await classAPI.deleteClass(classId);
        showSnackbar('수업이 삭제되었습니다.', 'success');
        fetchClasses();
      } catch (error) {
        console.error('수업 삭제 실패:', error);
        showSnackbar('수업 삭제에 실패했습니다.', 'error');
      }
    }
  };

  // 스낵바 표시
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          수업 관리
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <IconButton
              onClick={() => setViewMode('card')}
              color={viewMode === 'card' ? 'primary' : 'default'}
              size="small"
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              size="small"
            >
              <ViewListIcon />
            </IconButton>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            수업 등록
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Typography variant="h6" align="center" sx={{ py: 4 }}>
          수업 목록을 불러오는 중...
        </Typography>
      ) : classes.length === 0 ? (
        <Typography variant="h6" align="center" sx={{ py: 4, color: 'text.secondary' }}>
          등록된 수업이 없습니다. 수업을 등록해보세요.
        </Typography>
      ) : viewMode === 'card' ? (
        <Grid container spacing={3}>
          {classes.map((cls) => (
            <Grid item xs={12} sm={6} md={4} key={cls.id}>
              <Card sx={{ 
                backgroundColor: 'transparent',
                borderLeft: `5px solid ${cls.color || '#90caf9'}`,
                paddingLeft: 2
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon sx={{ mr: 1, color: '#333' }} />
                    <Typography variant="h6" component="div" sx={{ color: '#333' }}>
                      {cls.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ color: '#333' }} gutterBottom>
                    수업 시간: {cls.duration}분
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }} gutterBottom>
                    수강료: {cls.fee.toLocaleString()}원
                  </Typography>
                  {cls.memo && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      메모: {cls.memo}
                    </Typography>
                  )}
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(cls)}
                  >
                    수정
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(cls.id)}
                  >
                    삭제
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper>
          <List>
            {classes.map((cls, index) => (
              <React.Fragment key={cls.id}>
                <ListItem sx={{ 
                  backgroundColor: 'transparent',
                  borderLeft: `5px solid ${cls.color || '#90caf9'}`,
                  paddingLeft: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <SchoolIcon sx={{ mr: 1, color: '#333' }} />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="div" sx={{ color: '#333' }}>
                        {cls.name}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ color: '#333' }} component="span">
                          수업 시간: {cls.duration}분
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#333', ml: 2 }} component="span">
                          수강료: {cls.fee.toLocaleString()}원
                        </Typography>
                        {cls.memo && (
                          <Typography variant="body2" sx={{ color: '#333', mt: 0.5 }} component="div">
                            메모: {cls.memo}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(cls)}
                        variant="outlined"
                      >
                        수정
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(cls.id)}
                        variant="outlined"
                      >
                        삭제
                      </Button>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < classes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* 수업 등록/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingClass ? '수업 정보 수정' : '수업 등록'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="수업명"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="수업 시간 (분)"
                type="number"
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', parseInt(e.target.value) || 0)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="수강료 (원)"
                type="number"
                value={formData.fee}
                onChange={(e) => handleInputChange('fee', parseInt(e.target.value) || 0)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>대표 컬러</InputLabel>
                <Select
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  label="대표 컬러"
                >
                  <MenuItem value="#90caf9">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#90caf9', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#ffab91">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#ffab91', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#a5d6a7">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#a5d6a7', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#ffcc80">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#ffcc80', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#ce93d8">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#ce93d8', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#80cbc4">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#80cbc4', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#bcaaa4">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#bcaaa4', borderRadius: 1 }} />
                  </MenuItem>
                  <MenuItem value="#b0bec5">
                    <Box sx={{ width: 20, height: 20, backgroundColor: '#b0bec5', borderRadius: 1 }} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="메모"
                multiline
                rows={3}
                value={formData.memo}
                onChange={(e) => handleInputChange('memo', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSave} variant="contained">
            {editingClass ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassManagement;
